import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
// Services
import { RuleEngineApiService } from './rules-api.service';
// Models
import {
	OrderRulesModel,
	RuleCommentModel,
	RuleComponentDataInput,
	RuleComponentDictionary,
	RuleComponentInitModel,
	RuleCreateModel,
	RuleModel,
	RULE_STATUS,
	StipModel
} from '../models';

@Injectable()
export class RulesService {

	initComponent = new Subject();
	showNotification = new Subject();
	updateEvent = new Subject();
	rulesExecuted$: Subject<string> = new Subject();

	onSetOnRevised = new Subject();
	onSetRulesSelected = new Subject();
	onRulesEvents = new Subject();

	data: RuleComponentDataInput;
	orderDetail: any;
	currentUser: any;
	rules: any; // not used for now, its the same that lives inside rules.component
	rulesNotifications: any[];
	rulesSelected: OrderRulesModel; // main order ruleset
	rulesEvents: any[];
	defaultRuleSet: string; // RuleSet used as default if the user didnt provide any ruleset to the services
	// For the pipes: strings to be used for filter or labels
	dictionary = new RuleComponentDictionary();
	isEditing = false;

	onRevised: boolean;
	canFilterSsr: boolean;
	isRuleRunning: any;
	runningRules: boolean;
	deferredRuleIndicator: string;

	get sharedData(): any {
		return {
			data: this.data,
			orderDetail: this.orderDetail,
			currentUser: this.currentUser,
			rules: this.rules,
			rulesNotifications: this.rulesNotifications,
			rulesSelected: this.rulesSelected,
			rulesEvents: this.rulesEvents,
			defaultRuleSet: this.defaultRuleSet
		};
	}

	constructor(private rulesApi: RuleEngineApiService) {
		//
	}

	/**
	 * doc must exist
	 *
	 * @param data RuleComponentInitModel
	 */
	public init(data: RuleComponentInitModel): void {
		this.defaultRuleSet = data.defaultRuleSet;
		this.initComponent.next(true);
	}

	/**
	 * Set currentUser
	 *
	 * @param user
	 */
	public setCurrentUser(user: any): void {
		this.currentUser = user;
	}

	/**
	 * Set the orderDetail variable & canFilterSsr
	 *
	 * @param orderDetail
	 */
	public setOrderInbox(orderDetail: any): void {
		if (orderDetail) {
			this.orderDetail = orderDetail;
			this.canFilterSsr = this.orderDetail.hasSsrRules;
		}
	}

	/**
	 *
	 * @param isEdit
	 */
	public setIsEditing(isEdit: boolean): void {
		this.isEditing = isEdit;
	}

	/**
	 * Set the data variable
	 *
	 * @param data
	 */
	public setData(data: RuleComponentDataInput): void {
		if (data) {
			this.data = data;

			if (!('targets' in this.data) || !this.data.targets) {
				this.data.targets = {
					data: null,
					primary: null
				};
			}
		} else {
			this.data = new RuleComponentDataInput();
		}
	}

	/**
	 * Set the data variable
	 *
	 * @param rulesLog
	 */
	public setRulesNotifications(rulesLog: any): void {
		if (rulesLog) {
			this.rulesNotifications = rulesLog;
		}
	}

	/**
	 * doc must exist
	 *
	 * @param rules
	 */
	public setOrderRules(rules: OrderRulesModel): void {
		if (rules) {
			const nRules = this.getOrderRulesFiltered(rules);

			this.rulesSelected = nRules;
			this.onSetRulesSelected.next(this.rulesSelected);
		}
	}

	/**
	 * doc must exist
	 *
	 * @param onRevised
	 */
	public setOnRevised(onRevised: boolean): void {
		this.onRevised = onRevised;
		this.onSetOnRevised.next(onRevised);
	}

	/**
	 * doc must exist
	 *
	 * @param events
	 */
	public setRulesEvents(events: any): void {
		this.rulesEvents = events;
		this.onRulesEvents.next(events);
	}

	/**
	 * doc must exist
	 *
	 * @param dictionary
	 */
	public setDictionary(dictionary: RuleComponentDictionary): void {
		this.dictionary = dictionary;
	}

	// Component functions - Move to other file

	/**
	 * Update rule status
	 *
	 * @param violation @see RuleModel
	 * @param status
	 * @returns
	 */
	public updateRuleStatus(violation: RuleModel, status: RULE_STATUS): Promise<any> {
		return new Promise((resolve, reject) => {
			const data = {
				sharedData: this.sharedData,
				violation,
				status
			};

			this.rulesApi
				.updateRuleStatus(data)
				.subscribe(response => {
					if (response.isValid || response.success || response.succeed) {
						resolve(response);
					} else {
						reject();
					}
				});
		});
	}

	/**
	 * doc must exist
	 *
	 * @param ruleset
	 */
	public rulesetForceExecution(rulesets?: any[]): Promise<any> {
		return new Promise((resolve, reject) => {
			const data = {
				sharedData: this.sharedData,
				rulesets
			};

			this.rulesApi
				.rulesetsForceExecution(data)
				.subscribe(response => {
					if (response.isValid || response.success || response.succeed) {
						resolve(response);
					} else {
						reject();
					}
				});
		});
	}

	/**
	 * doc must exist
	 *
	 * @returns
	 */
	public addRule(ruleData: RuleCreateModel): Promise<any> {
		return new Promise( (resolve, reject) => {
			const data = {
				sharedData: this.sharedData,
				rule: ruleData
			};

			this.rulesApi
				.createRule(data)
				.subscribe(response => {
					// need standarize
					// isValid = succeed = success
					if (response.isValid || response.success || response.succeed) {
						const rule = {
							id: response.key
						};

						resolve(rule);
					} else {
						reject();
					}
				});
		});
	}

	/**
	 * add new comment to a Rule
	 *
	 * @param violation @see RuleModel
	 * @param comment
	 * @returns
	 */
	public addRuleComment(violation: RuleModel, comment: string): Promise<any> {
		return new Promise( (resolve, reject) => {
			const data = {
				sharedData: this.sharedData,
				violation,
				comment
			};

			this.rulesApi
				.createRuleComment(data)
				.subscribe(response => {
					// need standarize
					// isValid = succeed = success
					if (response.isValid || response.success || response.succeed) {
						const note = {
							id: response.key,
							note: comment,
							created: new Date(),
							updated: null,
							targets: []
						};

						resolve(note);
					} else {
						reject();
					}
				});
		});
	}

	/**
	 * doc must exist
	 *
	 * @param violation @see RuleModel
	 * @param comments RuleCommentModel[]
	 * @returns
	 */
	public deleteRuleComments(violation: RuleModel, comments: RuleCommentModel[]): Promise<any> {
		return new Promise( (resolve, reject) => {
			const data = {
				sharedData: this.sharedData,
				violation,
				comments
			};

			try {
				this.rulesApi
					.deleteRuleComments(data)
					.subscribe(response => {
						resolve(response);
					});
			} catch (error) {
				reject();
			}
		});
	}

	/**
	 * add new comment to a Rule
	 *
	 * @param violation @see RuleModel
	 * @param comment
	 * @returns
	 */
	public addRuleStip(violation: RuleModel, stip: StipModel): Promise<any> {
		return new Promise( (resolve, reject) => {
			const data = {
				sharedData: this.sharedData,
				violation,
				stip
			};

			this.rulesApi
				.createRuleStip(data)
				.subscribe(response => {
					// need standarize
					// isValid = succeed = success
					if (response.isValid || response.success || response.succeed) {
						const nStip = {
							id: response.key,
							stipulation: stip.stipulation,
							created: new Date(),
							updated: null,
							overwritten: stip.overwritten,
							ruleName: violation.ruleName
						};

						resolve(nStip);
					} else {
						reject();
					}
				});
		});
	}

	/**
	 * doc must exist
	 *
	 * @param violation @see RuleModel
	 * @param stips StipModel[]
	 * @returns
	 */
	public deleteRuleStips(violation: RuleModel, stips: StipModel[]): Promise<any> {
		return new Promise( (resolve, reject) => {
			const data = {
				sharedData: this.sharedData,
				violation,
				stips
			};

			try {
				this.rulesApi
					.deleteRuleStips(data)
					.subscribe(response => {
						resolve(response);
					});
			} catch (error) {
				reject();
			}
		});
	}

	// Rule Engine Utils - Move to other file
	/**
	 * Returns the Sorted Array of comments by descending most recent date.
	 *
	 * @param items (RuleCommentModel | StipModel)[]
	 * @returns
	 */
	sortRuleItemsByDescDate(items: (RuleCommentModel | StipModel)[]): (RuleCommentModel | StipModel)[] {
		if (items === null) {
			return [];
		}
		return [...items].sort((a, b) => {
			a = {
				...a,
				created: new Date(a.created)
			};
			b = {
				...b,
				created: new Date(b.created)
			};
			return b.created.getTime() - a.created.getTime();
		});
	}

	/**
	 * Filter the orderRules violations
	 *
	 * @param orderRules see @OrderRulesModel
	 */
	getOrderRulesFiltered(orderRules: OrderRulesModel): OrderRulesModel {
		const nRules: OrderRulesModel = JSON.parse(JSON.stringify(orderRules));
		const filtered = this.rulesApi.getCustomRulesFilter(nRules.violations);

		nRules.violations = filtered;

		return nRules;
	}
}
